import "./Events.scss";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from "react";
import PageTitle from "../../../components/PageTitle/PageTitle";
import { Modal } from "../../../components/Modal/Modal";
import AlertModal from "../../../components/AlertModal/AlertModal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import {
  DynamicFileInput,
  DynamicImagePreview,
  DynamicSelect,
  DynamicTextarea,
  DynamicInput,
} from "../../../components/Inputs/Inputs";
import Button from "../../../components/Button/Button";
import { AuthContext } from "../../../contexts/AuthContext";
import { resolveUrl } from "../../../utils/resolveUrl";
import ImagePlaceholder from "../../../assets/image_placeholder.png";
import { CiMapPin, CiEdit } from "react-icons/ci";
import DisplayInfo from "../../../components/DisplayInfo/DisplayInfo";

const emptyCreate = {
  // required (texto)
  name_pt: "",
  name_en: "",
  name_es: "",
  description_pt: "",
  description_en: "",
  description_es: "",
  location: "",
  file: null,
  previewUrl: "",
};

const emptyEdit = {
  id: null,
  name_pt: "",
  name_en: "",
  name_es: "",
  description_pt: "",
  description_en: "",
  description_es: "",
  location: "",
  image_url: "",
  file: null,
  previewUrl: "",
};

export default function EventRooms() {
  // delete confirmation
  const {
    axiosAuth,
    accessibleGroups = [],
    accessibleHotels = [],
    currentUser,
  } = useContext(AuthContext);
  const isHotelAdmin = currentUser?.role === "hotel_admin";

  // filtros
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [hotelsList, setHotelsList] = useState([]);
  const [selectedHotelId, setSelectedHotelId] = useState("");

  // dados
  const [rooms, setRooms] = useState([]);

  // ui
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // modais
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [confirm, setConfirm] = useState({ open: false, id: null });
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ ...emptyCreate });
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ ...emptyEdit });

  function openError(title, message) {
    setErrorModal({ open: true, title, message });
  }

  // options filtros
  const groupOptions = useMemo(
    () => accessibleGroups.map((g) => ({ value: String(g.id), label: g.name })),
    [accessibleGroups]
  );

  const hotelOptions = useMemo(() => {
    let list = hotelsList;
    if (isHotelAdmin && accessibleHotels?.length) {
      const allowed = new Set(accessibleHotels.map((h) => h.id));
      list = list.filter((h) => allowed.has(h.id));
    }
    return list.map((h) => ({ value: String(h.id), label: h.name }));
  }, [hotelsList, isHotelAdmin, accessibleHotels]);

  // carrega hotéis quando escolhe o grupo
  useEffect(() => {
    let cancelled = false;
    setSelectedHotelId("");
    setRooms([]);

    if (!selectedGroupId) {
      setHotelsList([]);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const { data } = await axiosAuth.get(
          `/hotels/group/${selectedGroupId}`
        );
        const fetched = data || [];

        // guarda a lista completa no estado (fonte de verdade)
        setHotelsList(fetched);

        // calcula a lista elegível para seleção automática (aplica regra de hotel_admin)
        let eligible = fetched;
        if (isHotelAdmin && accessibleHotels?.length) {
          const allowed = new Set(accessibleHotels.map((h) => h.id));
          eligible = fetched.filter((h) => allowed.has(h.id));
        }

        // seleciona o primeiro se existir
        const first = eligible[0]?.id;
        setSelectedHotelId(first ? String(first) : "");
      } catch (err) {
        openError(
          "Erro ao carregar hotéis do grupo",
          err?.response?.data?.detail || err.message
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedGroupId, axiosAuth, isHotelAdmin, accessibleHotels]);

  // carrega salas quando escolhe hotel
  useEffect(() => {
    setRooms([]);
    if (!selectedHotelId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axiosAuth.get(
          `/event-rooms/hotel/${selectedHotelId}`
        );
        setRooms(data || []);
      } catch (err) {
        openError(
          "Erro ao carregar salas de eventos",
          err?.response?.data?.detail || err.message
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedHotelId, axiosAuth]);

  useEffect(() => {
    if (accessibleGroups.length > 1 && !selectedGroupId) {
      setSelectedGroupId(String(accessibleGroups[0].id));
    }
  }, [accessibleGroups, selectedGroupId]);

  // handlers de criação
  function openCreateModal() {
    if (createForm.previewUrl) URL.revokeObjectURL(createForm.previewUrl);
    setCreateForm({ ...emptyCreate });
    setIsCreateOpen(true);
  }
  function updateCreate(name, value) {
    setCreateForm((p) => ({ ...p, [name]: value }));
  }
  function handleCreateImage(e) {
    const file = e.target.files?.[0] ?? null;
    if (createForm.previewUrl) URL.revokeObjectURL(createForm.previewUrl);
    setCreateForm((p) => ({
      ...p,
      file,
      previewUrl: file ? URL.createObjectURL(file) : "",
    }));
  }

  async function handleSubmitCreate() {
    if (!selectedGroupId || !selectedHotelId) {
      openError("Validação", "Selecione primeiro o grupo e o hotel.");
      return;
    }
    const required =
      createForm.name_pt &&
      createForm.name_en &&
      createForm.name_es &&
      createForm.description_pt &&
      createForm.description_en &&
      createForm.description_es;
    if (!required) {
      openError(
        "Validação",
        "Preencha todos os campos de nome e descrição (PT/EN/ES)."
      );
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("group_id", String(selectedGroupId));
      fd.append("hotel_id", String(selectedHotelId));
      fd.append("name_pt", createForm.name_pt);
      fd.append("name_en", createForm.name_en);
      fd.append("name_es", createForm.name_es);
      fd.append("description_pt", createForm.description_pt);
      fd.append("description_en", createForm.description_en);
      fd.append("description_es", createForm.description_es);
      fd.append("location", createForm.location ?? "");
      if (createForm.file) fd.append("image", createForm.file);

      await axiosAuth.post("/event-rooms/", fd);

      const { data } = await axiosAuth.get(
        `/event-rooms/hotel/${selectedHotelId}`
      );
      setRooms(data || []);
      setIsCreateOpen(false);
    } catch (err) {
      openError(
        "Erro ao criar sala",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
      if (createForm.previewUrl) {
        URL.revokeObjectURL(createForm.previewUrl);
        setCreateForm((p) => ({ ...p, previewUrl: "" }));
      }
    }
  }

  // handlers de edição
  function openEditModal(r) {
    if (editForm.previewUrl) URL.revokeObjectURL(editForm.previewUrl);
    setEditForm({
      id: r.id,
      name_pt: r.name_pt || "",
      name_en: r.name_en || "",
      name_es: r.name_es || "",
      description_pt: r.description_pt || "",
      description_en: r.description_en || "",
      description_es: r.description_es || "",
      location: r.location || "",
      image_url: r.image_url || "",
      file: null,
      previewUrl: "",
    });
    setIsEditOpen(true);
  }
  function updateEdit(name, value) {
    setEditForm((p) => ({ ...p, [name]: value }));
  }
  function handleEditImage(e) {
    const file = e.target.files?.[0] ?? null;
    if (editForm.previewUrl) URL.revokeObjectURL(editForm.previewUrl);
    setEditForm((p) => ({
      ...p,
      file,
      previewUrl: file ? URL.createObjectURL(file) : "",
    }));
  }

  async function handleSubmitEdit() {
    if (!editForm.id) return;
    const required =
      editForm.name_pt &&
      editForm.name_en &&
      editForm.name_es &&
      editForm.description_pt &&
      editForm.description_en &&
      editForm.description_es;
    if (!required) {
      openError(
        "Validação",
        "Preencha todos os campos de nome e descrição (PT/EN/ES)."
      );
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name_pt", editForm.name_pt);
      fd.append("name_en", editForm.name_en);
      fd.append("name_es", editForm.name_es);
      fd.append("description_pt", editForm.description_pt);
      fd.append("description_en", editForm.description_en);
      fd.append("description_es", editForm.description_es);
      if (editForm.location != null) fd.append("location", editForm.location);
      if (editForm.file) fd.append("image", editForm.file);

      await axiosAuth.put(`/event-rooms/${editForm.id}`, fd);

      const { data } = await axiosAuth.get(
        `/event-rooms/hotel/${selectedHotelId}`
      );
      setRooms(data || []);
      setIsEditOpen(false);
    } catch (err) {
      openError(
        "Erro ao guardar sala",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
      if (editForm.previewUrl) {
        URL.revokeObjectURL(editForm.previewUrl);
        setEditForm((p) => ({ ...p, previewUrl: "" }));
      }
    }
  }

  // delete
  function askDelete(id) {
    setConfirm({ open: true, id });
  }
  async function confirmDelete() {
    const id = confirm.id;
    if (!id) return;
    try {
      await axiosAuth.delete(`/event-rooms/${id}`);
      const { data } = await axiosAuth.get(
        `/event-rooms/hotel/${selectedHotelId}`
      );
      setRooms(data || []);
      setIsEditOpen(false);
    } catch (err) {
      openError(
        "Erro ao apagar sala",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setConfirm({ open: false, id: null });
    }
  }

  const currentEditPreview =
    editForm.previewUrl ||
    (editForm.image_url ? resolveUrl(editForm.image_url) : "");

  return (
    <div className="events-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={() => setErrorModal({ open: false, title: "", message: "" })}
        />
      )}
      {confirm.open && (
        <ConfirmationModal
          title="Apagar sala de eventos?"
          variant="danger"
          message="Deseja apagar esta sala?"
          secText="Fechar"
          varText="Apagar"
          onCancel={() => setConfirm({ open: false, id: null })}
          onConfirm={confirmDelete}
        />
      )}
      <PageTitle link="" title="Salas de eventos" />
      <div className="filters">
        <div className="left">
          <DynamicSelect
            label="Cliente"
            placeholder="Escolher um grupo"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            options={groupOptions}
            disabled={loading}
          />
          <DynamicSelect
            label="Hotel"
            placeholder="Escolher um hotel"
            value={selectedHotelId}
            onChange={(e) => setSelectedHotelId(e.target.value)}
            options={hotelOptions}
            disabled={loading || !selectedGroupId}
          />
        </div>
        <div className="right">
          {selectedGroupId && selectedHotelId && (
            <Button type="button" onClick={openCreateModal} disabled={loading}>
              Nova sala
            </Button>
          )}
        </div>
      </div>

      <div className="container">
        {rooms.map((r) => (
          <div key={r.id} className="event-card">
            <div className="image">
              {r.image_url ? (
                <img src={resolveUrl(r.image_url)} alt={r.name_pt} />
              ) : (
                <img src={ImagePlaceholder} alt="Image Placeholder" />
              )}
            </div>
            <div className="content">
              <h3>{r.name_pt}</h3>
              <div className="location">
                <CiMapPin size={14} />
                <p>{r.location || "-"}</p>
              </div>
              <div className="edit">
                <div className="button" onClick={() => openEditModal(r)}>
                  <button>Editar</button>
                  <span>
                    <CiEdit size={14} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!loading && selectedHotelId && rooms.length === 0 && (
        <DisplayInfo
          variant="info"
          message="Sem salas adicionadas a este hotel."
        />
      )}

      {isCreateOpen && (
        <Modal
          isVisible={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          title="Nova sala de eventos"
          saveText="Adicionar"
          onSave={handleSubmitCreate}
          disableActions={saving}
        >
          <div className="row">
            <DynamicImagePreview imageSrc={createForm.previewUrl} />
          </div>
          <div className="row">
            <DynamicFileInput
              required
              name="newImage"
              label="Imagem:"
              onChange={handleCreateImage}
            />
          </div>
          <div className="row three-col">
            <DynamicInput
              label="Nome (PT)"
              name="name_pt"
              required
              value={createForm.name_pt}
              onChange={updateCreate}
            />
            <DynamicInput
              label="Nome (EN)"
              name="name_en"
              required
              value={createForm.name_en}
              onChange={updateCreate}
            />
            <DynamicInput
              label="Nome (ES)"
              name="name_es"
              required
              value={createForm.name_es}
              onChange={updateCreate}
            />
          </div>
          <div className="row">
            <DynamicInput
              label="Localização"
              name="location"
              value={createForm.location}
              onChange={updateCreate}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (PT)"
              name="description_pt"
              rows={4}
              required
              value={createForm.description_pt}
              onChange={updateCreate}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (EN)"
              name="description_en"
              rows={4}
              required
              value={createForm.description_en}
              onChange={updateCreate}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (ES)"
              name="description_es"
              rows={4}
              required
              value={createForm.description_es}
              onChange={updateCreate}
            />
          </div>
        </Modal>
      )}

      {isEditOpen && (
        <Modal
          isVisible={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          title="Editar sala de eventos"
          saveText="Guardar"
          onSave={handleSubmitEdit}
          deleteText="Apagar"
          onDelete={() => askDelete(editForm.id)}
          disableActions={saving}
        >
          <div className="row">
            <DynamicImagePreview imageSrc={currentEditPreview} />
          </div>
          <div className="row">
            <DynamicFileInput
              label="Nova imagem:"
              onChange={handleEditImage}
              name="newImage"
            />
          </div>
          <div className="row three-col">
            <DynamicInput
              label="Nome (PT)"
              name="name_pt"
              required
              value={editForm.name_pt}
              onChange={updateEdit}
            />
            <DynamicInput
              label="Nome (EN)"
              name="name_en"
              required
              value={editForm.name_en}
              onChange={updateEdit}
            />
            <DynamicInput
              label="Nome (ES)"
              name="name_es"
              required
              value={editForm.name_es}
              onChange={updateEdit}
            />
          </div>
          <div className="row">
            <DynamicInput
              label="Localização"
              name="location"
              value={editForm.location}
              onChange={updateEdit}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (PT)"
              name="description_pt"
              rows={4}
              required
              value={editForm.description_pt}
              onChange={updateEdit}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (EN)"
              name="description_en"
              rows={4}
              required
              value={editForm.description_en}
              onChange={updateEdit}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (ES)"
              name="description_es"
              rows={4}
              required
              value={editForm.description_es}
              onChange={updateEdit}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
